import { useToast } from '@chakra-ui/react';
import { AxiosResponse } from 'axios';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { PAGE_SIZE } from '~/constants';
import { EquipmentView, Pagination } from '~/types';
import { queryClient } from '~/utils/react-query';
import {
  createEquipment,
  deleteDuplicates,
  deleteEquipment,
  getEquipmentById,
  getEquipments,
  updateEquipmentById,
} from '../../services';
import { CreatePayload, DeletePayload, UpdatePayload } from './types';

export function useEquipments(
  page: number,
  search = '',
  pageSize: number = PAGE_SIZE,
  options: UseQueryOptions<Pagination<EquipmentView>, unknown> = {},
): UseQueryResult<Pagination<EquipmentView>, unknown> {
  return useQuery<Pagination<EquipmentView>, unknown>(
    ['equipments', page, search, pageSize],
    () => getEquipments(page, search, pageSize),
    {
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
    },
  );
}

export function useEquipmentById(
  id: string,
  options: UseQueryOptions<EquipmentView, unknown> = {},
): UseQueryResult<EquipmentView, unknown> {
  const toast = useToast();

  return useQuery<EquipmentView, unknown>(
    ['equipment', id],
    () => getEquipmentById(id),
    {
      enabled: !!id,
      staleTime: 1000 * 60 * 10, // 10 minutes
      ...options,
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro ao obter dados, tente novamente',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useUpdateEquipment(): UseMutationResult<
  AxiosResponse<any>,
  unknown,
  UpdatePayload,
  unknown
> {
  const toast = useToast();

  return useMutation(
    async ({ id, formData }: UpdatePayload) =>
      updateEquipmentById(id, formData),

    {
      onSuccess: () => {
        toast({
          title: 'Alterado com sucesso',
          description: 'Dados alterados com sucesso!',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('equipments');
        queryClient.invalidateQueries('equipment');
      },
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro ao atualizar dados, tente novamente',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useCreateEquipment(): UseMutationResult<
  AxiosResponse<any>,
  unknown,
  CreatePayload,
  unknown
> {
  const toast = useToast();

  return useMutation(
    async ({ formData }: CreatePayload) => createEquipment(formData),
    {
      onSuccess: () => {
        toast({
          title: 'Criado com sucesso',
          description: 'equipamento criado com sucesso!',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('equipments');
      },
      onError: () => {
        toast({
          title: 'Erro',
          description: 'Erro ao criar equipamento, tente novamente',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );
}

export function useDeleteEquipment(): UseMutationResult<
  AxiosResponse<any>,
  unknown,
  DeletePayload,
  unknown
> {
  const toast = useToast();

  return useMutation(async ({ id }: DeletePayload) => deleteEquipment(id), {
    onSuccess: () => {
      toast({
        title: 'Excluido com sucesso',
        description: 'equipamento excluido com sucesso!',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      queryClient.invalidateQueries('equipments');
      queryClient.invalidateQueries('equipment');
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Erro ao excluir equipamento, tente novamente',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });
}

export function useDeleteEquipmentDuplicates(): UseMutationResult<
  AxiosResponse<any>,
  unknown,
  unknown,
  unknown
> {
  const toast = useToast();

  return useMutation(async () => deleteDuplicates(), {
    onSuccess: () => {
      toast({
        title: 'Duplicações removidas',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      queryClient.invalidateQueries('equipments');
      queryClient.invalidateQueries('equipment');
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Erro ao excluir duplicações, tente novamente',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });
}
